import axios from 'axios';
import { colorSchema } from 'utils/style';
import config from 'config';
import driverIcon from './driver-icon.png';
import { getTenantPreLoginConfig } from 'tenant/teanantPreLoginConfig';

const DONE_STATUSES = ['DONE', 'DONE_WITHOUT_MANIFEST'];

const getLatLng = (stop) => {
  const lat = stop && stop.position && stop.position.lat;
  const lng = stop && stop.position && stop.position.lng;
  return new window.google.maps.LatLng(lat, lng);
};

const getIcon = ({ status }) => {
  const ICONS = {
    PENDING: {
      path: 'M9.82544769,0 C15.2507294,0 19.6487892,4.39805982 19.6487892,9.82334156 C19.6487892,11.3679794 19.2845358,12.8908482 18.5856021,14.2683088 L10.9551624,29.3064017 C10.7392601,29.7319026 10.30259,30 9.82544769,30 C9.34830534,30 8.91163529,29.7319026 8.69573298,29.3064017 L1.06529327,14.2683088 C-1.38959435,9.43020911 0.542380753,3.51807476 5.38048045,1.06318713 C6.75794106,0.364253416 8.28080985,0 9.82544769,0 Z M10,8 C8.8954305,8 8,8.8954305 8,10 C8,11.1045695 8.8954305,12 10,12 C11.1045695,12 12,11.1045695 12,10 C12,8.8954305 11.1045695,8 10,8 Z',
      anchor: new window.google.maps.Point(10, 0),
    },
    DONE: {
      path: 'M9.82544769,0 C15.2507294,0 19.6487892,4.39805982 19.6487892,9.82334156 C19.6487892,11.3679794 19.2845358,12.8908482 18.5856021,14.2683088 L10.9551624,29.3064017 C10.7392601,29.7319026 10.30259,30 9.82544769,30 C9.34830534,30 8.91163529,29.7319026 8.69573298,29.3064017 L1.06529327,14.2683088 C-1.38959435,9.43020911 0.542380753,3.51807476 5.38048045,1.06318713 C6.75794106,0.364253416 8.28080985,0 9.82544769,0 Z M14.7935181,6.6532833 C14.4501195,6.42165802 13.9839705,6.5122682 13.7523452,6.85566681 L13.7523452,6.85566681 L9.23621661,13.5511028 L6.1899571,10.3966087 C5.90222018,10.0986484 5.42741877,10.0903607 5.12945847,10.3780977 C4.83149817,10.6658346 4.82321048,11.140636 5.1109474,11.4385963 L5.1109474,11.4385963 L8.75790385,15.2151302 C8.77121436,15.2289137 8.78492517,15.2420772 8.79900026,15.2546203 C8.83545476,15.2927008 8.876506,15.3274967 8.92199258,15.3581778 C9.26539119,15.5898031 9.73154015,15.4991929 9.96316544,15.1557943 L9.96316544,15.1557943 L14.9959016,7.69445616 C15.2275269,7.35105755 15.1369167,6.88490859 14.7935181,6.6532833 Z',
      anchor: new window.google.maps.Point(10, 0),
    },
  };

  const stopStatus = DONE_STATUSES.includes(status) ? 'DONE' : 'PENDING';
  const icon = ICONS[stopStatus];

  return {
    ...icon,
    fillOpacity: 1,
    strokeColor: 'white',
    strokeOpacity: 1,
    strokeWeight: 2,
    fillColor: '#0140CD',
  };
};

export const getRoute = (markers) => {
  if (!markers || !markers.length) return {};

  const origin = getLatLng(markers[0]);
  const destination = getLatLng(markers[markers.length - 1]);
  const waypoints = markers
    .map((stop, key) => {
      if (key === 0 || key === markers.length - 1) {
        return null;
      }

      return {
        location: getLatLng(stop),
        stopover: true,
      };
    })
    .filter((notnull) => notnull);

  return {
    avoidFerries: true,
    origin,
    destination,
    waypoints,
    optimizeWaypoints: false,
    travelMode: window.google.maps.TravelMode.DRIVING,
  };
};

export const getDriverMarker = async (drivers, handleShowError) => {
  try {
    const [driver] = drivers || [];
    const { user_id: driverId, is_static: isStatic } = driver || {};
    if (!driverId || isStatic) {
      handleShowError(true);
      return null;
    }
    let { latitude, longitude } = driver?.last_position || {};
    if (!latitude || !longitude) {
      const GIS_HEADERS = {
        'X-Auth-Tenant-Name': getTenantPreLoginConfig().gis_headers,
      };
      const requestConfig = {
        headers: {
          ...GIS_HEADERS,
        },
      };

      const response = await axios.get(
        `${config.GIS_URL}gps/position/${driverId}`,
        requestConfig
      );
      const [pos] = response?.data?.data || [];
      latitude = pos.latitude;
      longitude = pos.longitude;
    }

    return {
      icon: {
        url: driverIcon,
        size: new window.google.maps.Size(24, 24),
        scaledSize: new window.google.maps.Size(24, 24),
      },
      position: {
        lat: latitude,
        lng: longitude,
      },
      zIndex: 2,
      clickable: false,
    };
  } catch (e) {
    handleShowError(true);
    console.log('error: ', e);
    return null;
  }
};

export const getStopMarkers = (stops) =>
  stops.map(({ status, address }, key) => ({
    icon: {
      ...getIcon({
        status,
      }),
      size: new window.google.maps.Size(20, 20),
      scaledSize: new window.google.maps.Size(20, 20),
    },
    position: {
      lat: address.latitude,
      lng: address.longitude,
    },
    zIndex: 2,
    clickable: false,
    key,
  }));

export const directionsOptions = {
  suppressMarkers: true,
  suppressInfoWindows: true,
  polylineOptions: {
    clickable: false,
    strokeColor: 'blue',
    strokeWeight: 4,
    strokeOpacity: 0,
    zIndex: 3,
    icons: [
      ...[25, 50, 75].map((value) => ({
        icon: {
          strokeColor: colorSchema('primary500'),
          strokeOpacity: 1,
          scale: 3,
        },
        offset: `${value}%`,
      })),
    ],
  },
};

export const mapOptions = {
  disableDefaultUI: true,
  backgroundColor: '#182a33',
  styles: [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#f2f6f8',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#223340',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'administrative',
      elementType: 'geometry',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'administrative.neighborhood',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.business',
      stylers: [
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi.business',
      elementType: 'geometry',
      stylers: [
        {
          color: '#cad6e1',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'poi.park',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'geometry',
      stylers: [
        {
          color: '#cad6e1',
        },
        {
          weight: 1,
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#91a6ba',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e1e8ef',
        },
        {
          visibility: 'on',
        },
      ],
    },
    {
      featureType: 'transit',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#deeaff',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
  ],
};
